import type * as CSS from 'csstype';
import type {CSSRuleObject} from 'tailwindcss/types/config';

type NestedProperties = {
    [K in string]?: CSS.Properties | NestedProperties;
};

type Properties = CSS.Properties | NestedProperties;

export const defineRule = (topLevelSelector: string, properties: Properties) => {
    const finalRules = {
        [topLevelSelector]: properties,
    } as CSSRuleObject;

    Object.defineProperty(finalRules, 'toString', {
        enumerable: false,
        configurable: false,
        value: () => topLevelSelector,
    });

    return finalRules;
};

/**
 * It has `symbol` to prevent mistakes where key is used in place of value
 * since we only need `string` values, ts will error if key is passed as value.
 */
type CSSVarSentinel = keyof CSS.Properties | symbol;
type CSSVarsRecord<S extends readonly string[]> = Record<S[number], CSSVarSentinel>;

export function vr(variable: CSSVarSentinel | `--${string}`, fallback?: string): string;
export function vr(name: string): CSSVarSentinel;
export function vr<S extends readonly [string, ...string[]]>(...vars: S): CSSVarsRecord<S>;
export function vr<S extends readonly [string, ...string[]]>(...vars: S) {
    const [first, ...rest] = vars;
    if (first.startsWith('--')) {
        const fallback = rest[0];
        return `var(${first}${fallback ? ',' + fallback : ''})`;
    }
    if (rest.length === 0) {
        return `--${first}` as CSSVarSentinel;
    }
    return Object.fromEntries(vars.map(key => [key, `--${key}`])) as CSSVarsRecord<S>;
}

/**
 * Helper for `@media` rules, designed to stay consistent between how screens used in tailwind and in JSS
 */
export const at = (breakpoint: string | {max: string} | {min: string} | {max: string; min: string}): string => {
    const br = breakpoint;
    if (typeof br === 'string') return `(min-width: ${br})`;
    if ('max' in br && !('min' in br)) return `(max-width: ${br.max})`;
    if (!('max' in br) && 'min' in br) return `(min-width: ${br.min})`;
    return `(min-width: ${br.min} and max-width: ${br.max})`;
};
at.reducedMotion = `(prefers-reduced-motion)`;
at.normalMotion = `(prefers-reduced-motion: no-preference)`;
at.lightTheme = `(prefers-color-scheme: light)`;
at.darkTheme = `(prefers-color-scheme: dark)`;

export const self = {
    hover: (properties: CSS.Properties<string>) => ({
        '@media (hover: hover)': {
            '&:hover': properties,
        },
    }),
    disabled: (properties: Properties) => ({'&:disabled': properties}),
    active: (properties: Properties) => ({'&:active': properties}),
    focus: (properties: Properties) => ({'&:focus': properties}),
    focusVisible: (properties: Properties) => ({'&:focus-visible': properties}),
    placeholder: (properties: Properties) => ({'&::placeholder': properties}),
    element: (name: string, properties: Properties) => ({[`&__${name}`]: properties}),
    modifier: (name: string, properties: Properties) => ({[`&--${name}`]: properties}),
} as const;

export const recipes = {
    ring(offset: string, width: string, color: string) {
        return {
            boxShadow: `0 0 0 ${offset} #fff, 0 0 0 calc(${offset} + ${width}) ${color}`,
        };
    },
} as const;
